import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import './EnhancedNotificationButton.css';

const EnhancedNotificationButton = ({ 
  refreshInterval = 30000, // 30 seconds
  showTooltip = true,
  animateOnNew = true 
}) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previousCount, setPreviousCount] = useState(0);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const navigate = useNavigate();

  const fetchNotificationCount = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('Authentication token not found');
        setLoading(false);
        return;
      }

      // You can modify this to add query params if needed
      // For example: ?is_read=false to get only unread count
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notification/count-notification`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newCount = data.count || data.unread_count || 0;
        
        // Check if there are new notifications
        if (animateOnNew && newCount > previousCount && previousCount !== 0) {
          setHasNewNotifications(true);
          // Reset animation after 3 seconds
          setTimeout(() => setHasNewNotifications(false), 3000);
        }
        
        setPreviousCount(notificationCount);
        setNotificationCount(newCount);
      } else {
        console.error('Failed to fetch notification count');
        setNotificationCount(0);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
      setNotificationCount(0);
    } finally {
      setLoading(false);
    }
  }, [notificationCount, previousCount, animateOnNew]);

  // Fetch count on component mount
  useEffect(() => {
    fetchNotificationCount();
  }, []);

  // Set up polling interval
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(fetchNotificationCount, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchNotificationCount, refreshInterval]);

  // Expose refresh function globally for other components
  useEffect(() => {
    window.refreshNotificationCount = fetchNotificationCount;
    
    return () => {
      if (window.refreshNotificationCount === fetchNotificationCount) {
        delete window.refreshNotificationCount;
      }
    };
  }, [fetchNotificationCount]);

  // Listen for page visibility changes to refresh when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotificationCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchNotificationCount]);

  const handleNotificationClick = () => {
    navigate("/notifications");
    // Mark as opened and refresh count after a short delay
    setTimeout(() => {
      fetchNotificationCount();
      setHasNewNotifications(false);
    }, 1000);
  };

  const getBadgeClass = () => {
    let className = 'notification-count-badge';
    
    if (hasNewNotifications) {
      className += ' new-notification';
    }
    
    if (notificationCount > 50) {
      className += ' high-count';
    } else if (notificationCount > 10) {
      className += ' medium-count';
    } else if (notificationCount > 0) {
      className += ' low-count';
    }
    
    return className;
  };

  const getTooltipText = () => {
    if (loading) return 'Loading notifications...';
    if (notificationCount === 0) return 'No unread notifications';
    if (notificationCount === 1) return '1 unread notification';
    return `${notificationCount} unread notifications`;
  };

  return (
    <button 
      onClick={handleNotificationClick}
      className={`notification-btn ${notificationCount > 0 ? 'has-notifications' : ''}`}
      disabled={loading}
      title={showTooltip ? getTooltipText() : ''}
      aria-label={getTooltipText()}
    >
      🔔
      {notificationCount > 0 && (
        <span className={getBadgeClass()}>
          {notificationCount > 99 ? '99+' : notificationCount}
        </span>
      )}
      {loading && (
        <span className="notification-loading-indicator">
          <span className="loading-dot"></span>
        </span>
      )}
    </button>
  );
};

// Hook for other components to refresh notification count
export const useNotificationRefresh = () => {
  return useCallback(() => {
    if (window.refreshNotificationCount) {
      window.refreshNotificationCount();
    }
  }, []);
};

export default EnhancedNotificationButton;